import { formatISODateToYYYYMMDD } from "@/utils/dateUtils";
import type {
  CreateManagedUserPayload,
  ManagedTrialDto,
  ManagedUserDto,
  UpdateManagedUserPayload,
} from "@/services/adminManageService";

import type {
  AdminStudyAssignment,
  AdminStudyPermissionGroup,
} from "./adminRolesPermissionsData";
import type {
  AdminAccountType,
  AdminStudyOption,
  AdminUser,
} from "./adminUsersAccessData";
import type { EditUserSettingsSubmitPayload } from "./EditUserSettingsModal";

const PLACEHOLDER_DATE = "—";
const PLACEHOLDER_INVITED_BY = { name: "Vivo", isVivo: true };
const DEFAULT_STUDY_ACCENT_COLOR = "#40B9DF";

const ROLE_DOT_COLORS: Record<string, string> = {
  full_access: "#0C98D0",
};
const DEFAULT_ROLE_DOT_COLOR = "#66928E";

export function formatRoleLabel(roleSlug: string) {
  return roleSlug.replace(/_/g, " ").toUpperCase();
}

export function getRoleDotColor(roleSlug: string) {
  return ROLE_DOT_COLORS[roleSlug] ?? DEFAULT_ROLE_DOT_COLOR;
}

export function getAdminUserDisplayName(user: {
  firstName?: string;
  lastName?: string;
  email: string;
}) {
  const fullName = [user.firstName, user.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return fullName || user.email;
}

function buildTrialLabel(trialId: string, trialName?: string) {
  const normalizedName = trialName?.trim();
  return normalizedName ? `${normalizedName} | ${trialId}` : trialId;
}

export function buildStudyOptionsFromManagedTrials(
  managedTrials: ManagedTrialDto[],
): AdminStudyOption[] {
  return managedTrials.map((trial) => ({
    id: trial.id,
    label: buildTrialLabel(trial.id, trial.name),
    roles: trial.roles,
  }));
}

export function buildTrialRolesById(
  managedTrials: ManagedTrialDto[],
): Record<string, string[]> {
  return Object.fromEntries(
    managedTrials.map((trial) => [trial.id, trial.roles]),
  );
}

export function buildCreateManagedUserPayload(
  invite: { email: string; accountType: AdminAccountType },
  sharedStudyAssignment?: { studyId: string; access: string },
): CreateManagedUserPayload {
  if (invite.accountType === "ADMIN") {
    return {
      email: invite.email,
      role_assignment: "admin",
      client_admin: true,
    };
  }

  if (!sharedStudyAssignment) {
    throw new Error(
      "User invites require a study and role assignment before submission.",
    );
  }

  return {
    email: invite.email,
    role_assignment: sharedStudyAssignment.access,
    trials: [sharedStudyAssignment.studyId],
    client_admin: false,
  };
}

export function mapManagedUserToAdminUser(
  user: ManagedUserDto,
  options: {
    canDeactivateAdmin: boolean;
    trialsById?: Map<string, ManagedTrialDto>;
  } = { canDeactivateAdmin: false },
): AdminUser {
  return {
    id: user.id,
    firstName: user.first_name,
    lastName: user.last_name,
    email: user.email,
    accountType: user.is_admin ? "ADMIN" : "USER",
    invitedOn: user.invited_on
      ? formatISODateToYYYYMMDD(user.invited_on)
      : PLACEHOLDER_DATE,
    invitedBy: user.invited_by
      ? { name: user.invited_by.name, isVivo: false }
      : PLACEHOLDER_INVITED_BY,
    canDeactivate: user.is_admin ? options.canDeactivateAdmin : true,
    studyPermissions: user.grants.map((grant) => ({
      id: `${user.id}-${grant.trial}`,
      trialId: grant.trial,
      studyLabel: buildTrialLabel(
        grant.trial,
        options.trialsById?.get(grant.trial)?.name,
      ),
      access: grant.role,
    })),
  };
}

export function mapManagedUsersToAdminUsers(
  users: ManagedUserDto[],
  managedTrials: ManagedTrialDto[] = [],
) {
  const adminCount = users.filter((user) => user.is_admin).length;
  const trialsById = new Map(
    managedTrials.map((trial) => [trial.id, trial] as const),
  );

  return users.map((user) =>
    mapManagedUserToAdminUser(user, {
      canDeactivateAdmin: adminCount > 1,
      trialsById,
    }),
  );
}

function groupPermissionsByRole(
  permissions: { trialId: string; role: string }[],
): [string, string[]][] {
  const trialIdsByRole = new Map<string, string[]>();
  for (const { trialId, role } of permissions) {
    const trialIds = trialIdsByRole.get(role) ?? [];
    trialIds.push(trialId);
    trialIdsByRole.set(role, trialIds);
  }
  return Array.from(trialIdsByRole.entries());
}

// The backend PATCH endpoint takes a single role_assignment per call, scoped
// to the trials listed in that call, and only touches client_admin when
// role_assignment is present (defaulting it to false otherwise). To avoid
// re-asserting unchanged grants (which can fail validation against a trial's
// role allowlist), only permissions whose role actually changed are PATCHed;
// an admin-status-only change instead uses a carrier PATCH over all retained
// grants, and a pure name edit uses a role_assignment-free request.
export function buildManagedUserUpdateRequests(
  payload: EditUserSettingsSubmitPayload,
  profileFields: { first_name?: string; last_name?: string },
  user: AdminUser,
): UpdateManagedUserPayload[] {
  if (payload.retainedPermissions.length === 0) {
    return [
      {
        role_assignment: "admin",
        client_admin: payload.clientAdmin,
        ...profileFields,
      },
    ];
  }

  const initialRoleByTrialId = new Map(
    user.studyPermissions.map((permission) => [
      permission.trialId,
      permission.access,
    ]),
  );
  const changed = payload.retainedPermissions.filter(
    ({ trialId, role }) => initialRoleByTrialId.get(trialId) !== role,
  );
  const clientAdminChanged =
    payload.clientAdmin !== (user.accountType === "ADMIN");

  const permissionsToGroup =
    changed.length > 0
      ? changed
      : clientAdminChanged
        ? payload.retainedPermissions
        : [];

  if (permissionsToGroup.length > 0) {
    return groupPermissionsByRole(permissionsToGroup).map(
      ([role, trialIds]) => ({
        role_assignment: role,
        trials: trialIds,
        client_admin: payload.clientAdmin,
        ...profileFields,
      }),
    );
  }

  const trimmedFirstName = payload.firstName.trim();
  const trimmedLastName = payload.lastName.trim();
  const nameChanged =
    trimmedFirstName !== user.firstName || trimmedLastName !== user.lastName;

  if (nameChanged && Object.keys(profileFields).length > 0) {
    return [{ ...profileFields }];
  }

  return [];
}

export function mapManagedTrialsToStudyPermissionGroups(
  managedTrials: ManagedTrialDto[],
  users: ManagedUserDto[],
): AdminStudyPermissionGroup[] {
  return managedTrials
    .map((trial) => {
      const assignments: AdminStudyAssignment[] = users.flatMap((user) =>
        user.grants
          .filter((grant) => grant.trial === trial.id)
          .map((grant) => ({
            id: `${trial.id}-${user.id}`,
            user: {
              id: user.id,
              firstName: user.first_name,
              lastName: user.last_name,
              email: user.email,
            },
            access: grant.role,
            dateAssigned: grant.date_assigned
              ? formatISODateToYYYYMMDD(grant.date_assigned)
              : PLACEHOLDER_DATE,
          })),
      );

      return {
        id: trial.id,
        studyName: trial.name.trim() || trial.id,
        trialId: trial.id,
        accentColor: DEFAULT_STUDY_ACCENT_COLOR,
        roles: trial.roles,
        assignments: assignments.sort((left, right) =>
          getAdminUserDisplayName(left.user).localeCompare(
            getAdminUserDisplayName(right.user),
          ),
        ),
      };
    })
    .sort((left, right) => left.studyName.localeCompare(right.studyName));
}
